'use client'

import { useEffect, useRef } from 'react'

/**
 * A single confetti burst, for the one moment in the product that has earned it:
 * a page going live.
 *
 * Canvas rather than a swarm of DOM nodes — a hundred elements animating at once
 * is a hundred things for the compositor to track, and none of them are content.
 * Nothing is exported but the burst; it fires once on mount, runs for about two
 * seconds, then cancels itself and frees the loop.
 *
 * Brand colours only. A rainbow here would be the one place in the product where
 * hues appear that exist nowhere else in it.
 */
const COLORS = ['#f97316', '#ef4444', '#fb923c', '#f87171', '#fafafa']

const COUNT = 90
const GRAVITY = 0.14
const DRAG = 0.992
const FADE_AFTER = 0.55

interface Piece {
  x: number
  y: number
  vx: number
  vy: number
  size: number
  color: string
  spin: number
  angle: number
}

export function Celebrate() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    // Asked for less motion: no burst at all. The screen still reads without it.
    if(window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')
    if(!canvas || !context) return

    // Backing store at device resolution, CSS box at layout resolution, so the
    // pieces are not soft on a retina screen.
    const ratio = Math.min(window.devicePixelRatio || 1, 2)
    const width = canvas.clientWidth
    const height = canvas.clientHeight
    canvas.width = width * ratio
    canvas.height = height * ratio
    context.scale(ratio, ratio)

    // Thrown from just above the middle, where the link card sits, rather than
    // from the top of the window — the burst should read as coming *from* the
    // thing being celebrated.
    const originX = width / 2
    const originY = height * 0.42

    const pieces: Piece[] = Array.from({ length: COUNT }, () => {
      const angle = Math.random() * Math.PI * 2
      const speed = 4 + Math.random() * 7
      return {
        x: originX,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 3,
        size: 4 + Math.random() * 5,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        spin: (Math.random() - 0.5) * 0.3,
        angle: Math.random() * Math.PI,
      }
    })

    let frame = 0
    let raf = 0
    const LIFETIME = 150

    const tick = () => {
      frame++
      context.clearRect(0, 0, width, height)

      const progress = frame / LIFETIME
      const alpha = progress < FADE_AFTER ? 1 : Math.max(0, 1 - (progress - FADE_AFTER) / (1 - FADE_AFTER))

      for(const piece of pieces) {
        piece.vy += GRAVITY
        piece.vx *= DRAG
        piece.vy *= DRAG
        piece.x += piece.vx
        piece.y += piece.vy
        piece.angle += piece.spin

        context.save()
        context.translate(piece.x, piece.y)
        context.rotate(piece.angle)
        context.globalAlpha = alpha
        context.fillStyle = piece.color
        // Rectangles, not circles: a flat strip catching a rotation reads as
        // paper, where a dot reads as a particle effect.
        context.fillRect(-piece.size / 2, -piece.size / 4, piece.size, piece.size / 2)
        context.restore()
      }

      if(frame < LIFETIME) raf = requestAnimationFrame(tick)
      else context.clearRect(0, 0, width, height)
    }

    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-50 size-full"
    />
  )
}
